import Company from "../../../classes/Company";
import Address from "../../../classes/Address";
import { db } from "../../../boot/firebase";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit as limitQuery,
  orderBy,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { call } from "../../../util/misc";

export const GET_COMPANY = async ({ dispatch }, id) => {
  return dispatch("GET_DOC", {
    converter: Company,
    path: Company.getPath(id),
    stateKey: "companies",
    id,
  });
};

export const GET_COMPANIES = (
  { dispatch },
  { conditions, limitCount, pagination }
) => {
  return dispatch("GET_COLLECTION", {
    converter: Company,
    stateKey: "companies",
    path: Company.getPath(),
    conditions,
    limitCount,
    pagination,
  });
};

export const SEARCH_COMPANIES = async (
  { commit, state },
  { search, limit = 20, type = null }
) => {
  if (!search) {
    const constraints = [orderBy("created", "desc"), limitQuery(limit)];
    if (type) {
      constraints.unshift(
        where("type", Array.isArray(type) ? "in" : "==", type)
      );
    }
    const snap = await getDocs(
      query(collection(db, Company.getPath()), ...constraints)
    );
    return snap.docs.map((docSnapshot) => {
      const company = new Company(docSnapshot.id, docSnapshot.data());
      commit("SET", {
        stateKey: "companies",
        stateID: docSnapshot.id,
        doc: company,
      });
      return company;
    });
  }

  let matches = Object.values(state.companies || {});
  if (type) {
    matches = matches.filter((company) =>
      Array.isArray(type)
        ? type.includes(company.data.type)
        : company.data.type === type
    );
  }

  const searchLower = search.toLowerCase();
  matches = matches.filter((company) => {
    if (company.data.name?.toLowerCase().includes(searchLower)) return true;
    if (
      company.data.emails?.some((email) =>
        email.address?.toLowerCase().includes(searchLower)
      )
    ) {
      return true;
    }
    if (company.data.phones?.some((phone) => phone.number?.includes(search))) {
      return true;
    }
    return false;
  });

  return matches.slice(0, limit);
};

export const LISTEN_COMPANY = ({ dispatch }, { id, next, error, unlisten }) => {
  return dispatch("LISTEN_DOC", {
    converter: Company,
    path: Company.getPath(id),
    stateKey: "companies",
    id,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_COMPANIES = ({ dispatch }, { next, error, unlisten }) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: Company,
    path: Company.getPath(),
    stateKey: "companies",
    next,
    error,
    unlisten,
  });
};

export const SAVE_COMPANY = async ({ commit, dispatch, state }, company) => {
  const saveDocuments = (existingData) => {
    return Promise.all([
      dispatch("SAVE_DOCUMENTS", {
        parentData: company.data,
        fieldPath: "creditApp",
        folderPath: company.path,
        isSingle: true,
        existingData,
      }),
      dispatch("SAVE_DOCUMENTS", {
        parentData: company.data,
        fieldPath: "creditReferences",
        folderPath: company.path,
        isSingle: false,
        existingData,
      }),
      dispatch("SAVE_DOCUMENTS", {
        parentData: company.data,
        fieldPath: "w9",
        folderPath: company.path,
        isSingle: true,
        existingData,
      }),
      dispatch("SAVE_DOCUMENTS", {
        parentData: company.data,
        fieldPath: "vendorDocs",
        folderPath: company.path,
        isSingle: false,
        existingData,
      }),
    ]);
  };

  if (!company.data.created) {
    // New Company
    company.data.created = new Date();
    company.data.createdBy = state.user.id;

    // A new company usually has no id → let doc() auto-generate. Passing
    // `undefined` as a path segment (the old `company.id || undefined`) throws
    // "Function doc() cannot be called with an empty path" in the Firebase v9
    // modular SDK, which broke company creation.
    const companies = collection(db, Company.getPath());
    const ref = company.id ? doc(companies, company.id) : doc(companies);
    company.id = ref.id;

    await saveDocuments();
    await setDoc(ref.withConverter(Company), company);
  } else {
    // Existing Company
    company.data.modified = new Date();
    company.data.modifiedBy = state.user.id;
    const current = await dispatch("GET_COMPANY", company.id);

    await saveDocuments(current.data);
    await setDoc(doc(db, company.path).withConverter(Company), company);
  }

  // If this save resolves a pending "add new address" request for this
  // company (launched from a ContactAddress field), detect the newly added
  // address so the originating field can auto-populate it on return.
  const pendingAddress = state.pendingAddressSelection;
  if (
    pendingAddress &&
    pendingAddress.contactID === company.id &&
    !pendingAddress.address
  ) {
    const existingKeys = new Set(pendingAddress.existingKeys || []);
    const newAddress = (company.data.addresses || []).find(
      (address) => !existingKeys.has(new Address(address).locationKey())
    );
    if (newAddress) {
      commit("SET_PENDING_ADDRESS", {
        ...newAddress,
        contact: company.id,
      });
    }
  }

  return company;
};

export const DELETE_COMPANY = async ({ commit }, id) => {
  await deleteDoc(doc(db, Company.getPath(id)));
  commit("REMOVE", { stateKey: "companies", id });
};

export const ADD_TO_COMPANY = async (context, { contactID, companyID }) => {
  return call("addToCompany", { contactID, companyID });
};
